import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "sonner";

export type GlobalConnectIntent = "curious" | "dating" | "serious";

export interface GlobalConnectSettings {
  id: string;
  user_id: string;
  is_enabled: boolean;
  intent_level: GlobalConnectIntent;
  intent_changed_at: string;
  open_to_countries: string[];
  languages_spoken: string[];
  created_at: string;
  updated_at: string;
}

export interface GlobalConnectLimits {
  pops_used: number;
  pops_remaining: number;
  likes_used: number;
  likes_remaining: number;
  matches_count: number;
  matches_remaining: number;
  countries_visited: string[];
  countries_remaining: number;
  can_pop: boolean;
  can_like: boolean;
}

export interface CountryStats {
  active_users: number;
  users_by_intent: {
    curious: number;
    dating: number;
    serious: number;
  };
  popular_cities: Array<{ name: string; id: string }> | null;
}

export const useGlobalConnect = () => {
  const { user } = useAuthContext();
  const [settings, setSettings] = useState<GlobalConnectSettings | null>(null);
  const [limits, setLimits] = useState<GlobalConnectLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentSession, setCurrentSession] = useState<string | null>(null);

  // Fetch user's global connect settings
  const fetchSettings = useCallback(async () => {
    if (!user) {
      setSettings(null);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("global_connect_settings")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    if (error && error.code !== "PGRST116") {
      console.error("Error fetching global connect settings:", error);
    }
    
    setSettings(data as GlobalConnectSettings | null);
    setLoading(false);
  }, [user]);

  // Fetch daily limits
  const fetchLimits = useCallback(async () => {
    if (!user) return;

    const { data, error } = await supabase.rpc("check_global_connect_limits", {
      user_uuid: user.id,
    });

    if (error) {
      console.error("Error fetching limits:", error);
      return;
    }

    setLimits(data as unknown as GlobalConnectLimits);
  }, [user]);

  useEffect(() => {
    fetchSettings();
    fetchLimits();
  }, [fetchSettings, fetchLimits]);

  // Enable Global Connect with intent
  const enableGlobalConnect = useCallback(
    async (intentLevel: GlobalConnectIntent) => {
      if (!user) return { error: new Error("Not authenticated") };

      const { data, error } = await supabase
        .from("global_connect_settings")
        .upsert(
          {
            user_id: user.id,
            is_enabled: true,
            intent_level: intentLevel,
            intent_changed_at: new Date().toISOString(),
          },
          { onConflict: "user_id" }
        )
        .select()
        .single();

      if (error) {
        toast.error("Failed to enable Global Connect");
        return { error };
      }

      setSettings(data as GlobalConnectSettings);
      toast.success("Global Connect enabled!");
      return { data };
    },
    [user]
  );

  // Update intent level (no cooldown - can be changed anytime)
  const updateIntent = useCallback(
    async (intentLevel: GlobalConnectIntent) => {
      if (!user || !settings) return { error: new Error("Not enabled") };

      const { data, error } = await supabase
        .from("global_connect_settings")
        .update({
          intent_level: intentLevel,
          intent_changed_at: new Date().toISOString(),
        })
        .eq("user_id", user.id)
        .select()
        .single();

      if (error) {
        toast.error("Failed to update intent");
        return { error };
      }

      setSettings(data as GlobalConnectSettings);
      toast.success("Intent updated!");
      return { data };
    },
    [user, settings]
  );

  // Disable Global Connect
  const disableGlobalConnect = useCallback(async () => {
    if (!user) return { error: new Error("Not authenticated") };

    const { error } = await supabase
      .from("global_connect_settings")
      .update({ is_enabled: false })
      .eq("user_id", user.id);

    if (error) {
      toast.error("Failed to disable Global Connect");
      return { error };
    }

    setSettings((prev) => (prev ? { ...prev, is_enabled: false } : null));
    toast.success("Global Connect disabled");
    return { error: null };
  }, [user]);

  // Start a session for a country
  const startSession = useCallback(
    async (countryCode: string) => {
      if (!user) return null;

      // Track country visit
      await supabase.rpc("increment_global_connect_usage", {
        user_uuid: user.id,
        action_type: "country_visit",
        country_code_param: countryCode,
      });

      const { data, error } = await supabase
        .from("global_connect_sessions")
        .insert({
          user_id: user.id,
          country_code: countryCode,
        })
        .select()
        .single();

      if (error) {
        console.error("Error starting session:", error);
        return null;
      }

      setCurrentSession(data.id);
      await fetchLimits();
      return data.id;
    },
    [user, fetchLimits]
  );

  // End current session
  const endSession = useCallback(async () => {
    if (!currentSession) return;

    await supabase
      .from("global_connect_sessions")
      .update({ ended_at: new Date().toISOString() })
      .eq("id", currentSession);

    setCurrentSession(null);
  }, [currentSession]);

  // Increment usage and check limits
  const useAction = useCallback(
    async (actionType: "pop" | "like" | "match") => {
      if (!user) return false;

      // Check if action is allowed
      if (limits) {
        if (actionType === "pop" && !limits.can_pop) {
          toast.error("Daily pop limit reached");
          return false;
        }
        if (actionType === "like" && !limits.can_like) {
          toast.error("Daily like limit reached");
          return false;
        }
      }

      await supabase.rpc("increment_global_connect_usage", {
        user_uuid: user.id,
        action_type: actionType,
        country_code_param: null,
      });

      await fetchLimits();
      return true;
    },
    [user, limits, fetchLimits]
  );

  // Get country stats
  const getCountryStats = useCallback(
    async (countryCode: string): Promise<CountryStats | null> => {
      const { data, error } = await supabase.rpc(
        "get_global_connect_country_stats",
        { country_code_param: countryCode }
      );

      if (error) {
        console.error("Error fetching country stats:", error);
        return null;
      }

      return data?.[0] as CountryStats | null;
    },
    []
  );

  return {
    settings,
    limits,
    loading,
    currentSession,
    isEnabled: settings?.is_enabled ?? false,
    intentLevel: settings?.intent_level ?? null,
    enableGlobalConnect,
    updateIntent,
    disableGlobalConnect,
    startSession,
    endSession,
    useAction,
    getCountryStats,
    refetch: fetchSettings,
    refetchLimits: fetchLimits,
  };
};

// Intent display helpers
export const intentLabels: Record<GlobalConnectIntent, string> = {
  curious: "Global Curious",
  dating: "Global Dating",
  serious: "Global Serious",
};

export const intentDescriptions: Record<GlobalConnectIntent, string> = {
  curious: "Open to chatting and cultural exchange",
  dating: "Open to long-distance romantic connection",
  serious: "Open to serious relationships across borders",
};

export const intentIcons: Record<GlobalConnectIntent, string> = {
  curious: "🌍",
  dating: "💬",
  serious: "💍",
};
